import { buildHref } from "@softmax/web-addresses";
import { buildEpisodes } from "../lib/episodes/build-episodes";
import { buildLeagueEpisodes } from "../lib/episodes/league-episodes";
import type { PlayEpisodesData } from "../lib/types";
import type { PlayApi } from "./play-api";
import type { PlayShellInternal } from "./play-shell";
export async function readPlayEpisodes({
  api,
  shell,
}: {
  readonly api: PlayApi;
  readonly shell: PlayShellInternal;
}): Promise<PlayEpisodesData> {
  const common = {
    league: shell.league,
    generatedAt: new Date().toISOString(),
  };
  const episodeHref = (episodeId: string) =>
    buildHref({ name: "episode", episodeId, game: shell.league.route.game });
  const replayHref = (episodeId: string) =>
    buildHref({
      name: "game.home",
      game: shell.league.route.game,
      search: new URLSearchParams({ e: episodeId }),
    });

  if (shell.isMember) {
    if (!shell.seat) return { ...common, kind: "no-policy" };

    const page = await api.myEpisodes({
      policyVersionId: shell.seat.policy_version_id,
      limit: 100,
    });

    if (!page) throw new Error("Your episodes are unavailable");

    return {
      ...common,
      kind: "personal",
      policyLabel: shell.seat.policy_label,
      episodes: buildEpisodes({
        entries: page.entries,
        viewer: {
          playerId: shell.seat.player_id,
          playerName: shell.seat.player_name,
        },
        nextCursor: page.next_cursor,
        episodeHrefOf: episodeHref,
        replayHrefOf: replayHref,
      }),
    };
  }

  if (!shell.divisionId) return { ...common, kind: "league", rows: [] };

  const rounds = await api.rounds({
    divisionId: shell.divisionId,
    status: "completed",
    limit: 1,
  });

  if (!rounds) throw new Error("League episodes are unavailable");

  const round = rounds.entries[0];

  if (!round) return { ...common, kind: "league", rows: [] };

  const page = await api.roundEpisodes({
    roundId: round.id,
    limit: 20,
  });

  if (!page) throw new Error("League episodes are unavailable");

  return {
    ...common,
    kind: "league",
    rows: buildLeagueEpisodes({
      entries: page.entries,
      episodeHrefOf: episodeHref,
      replayHrefOf: replayHref,
    }),
  };
}
